import React, { useState, useEffect } from 'react';
import { ShoppingCartIcon, CashIcon, CheckCircleIcon } from '@heroicons/react/outline';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addToCart } from '../../store/cartSlice';
import { toastService } from '../../services/toast';
import ReactDOM from 'react-dom';

const Modal = ({ onClose, children }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${window.scrollY}px`;

    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscKey);

    return () => {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <>
      <div 
        className="fixed inset-0 z-50"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)'
        }}
        onClick={onClose}
      />
      <div 
        className="fixed z-50 inset-0 overflow-y-auto"
      >
        <div 
          className="flex items-center justify-center min-h-screen p-4"
          onClick={onClose}
        >
          <div 
            className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-md relative"
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

const BookCard = ({ book }) => {
  const [showModal, setShowModal] = useState(false);
  const [addedBook, setAddedBook] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const discountPercentage = book.oldPrice ? Math.round(((book.oldPrice - book.price) / book.oldPrice) * 100) : 0;

  const handleAddToCart = (e) => {
    e.stopPropagation();
    dispatch(addToCart({ ...book, type: 'book' }));
    setAddedBook(book);
    setShowModal(true);
  };

  const handleBuyNow = (e) => {
    e.stopPropagation();
    dispatch(addToCart({ ...book, type: 'book' }));
    navigate('/cart');
  };

  const handleBookClick = () => {
    if (!showModal) {
      navigate(`/store/books/${book.id}`);
    }
  };

  const handleContinueShopping = () => {
    setShowModal(false);
    toastService.success('تم إضافة الكتاب إلى السلة');
  };

  const handleCheckout = () => {
    setShowModal(false);
    navigate('/cart');
  };

  return (
    <div 
      onClick={handleBookClick}
      className="group relative bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer overflow-hidden"
    >
      {/* Discount Badge */}
      {discountPercentage > 0 && (
        <div className="absolute top-2 right-2 bg-red-500 text-white text-sm font-bold px-2 py-1 rounded-full z-10">
          خصم {discountPercentage}%
        </div>
      )}
      
      {/* Book Cover */}
      <div className="w-full h-[340px] overflow-hidden bg-gray-100 dark:bg-gray-800/80">
        <img
          src={book.coverImage}
          alt={book.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </div>

      {/* Book Info */}
      <div className="p-5 space-y-3 bg-white dark:bg-gray-800/50">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white line-clamp-1">
          {book.title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {book.author}
        </p>

        {/* Price */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
            {book.price} ر.س
          </span>
          {book.oldPrice && (
            <span className="text-sm text-gray-500 line-through">
              {book.oldPrice} ر.س
            </span>
          )}
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={handleAddToCart}
            className="flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded focus:outline-none"
          >
            <ShoppingCartIcon className="h-4 w-4 ml-1" />
            للسلة
          </button>
          <button
            onClick={handleBuyNow}
            className="flex items-center justify-center px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-500 border border-primary-600 dark:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900 rounded focus:outline-none"
          >
            <CashIcon className="h-4 w-4 ml-1" />
            اشتري
          </button>
        </div>
      </div>

      {/* Modal after adding to cart */}
      {showModal && addedBook && (
        <Modal onClose={handleContinueShopping}>
          <div className="text-center" onClick={(e) => e.stopPropagation()}>
            <CheckCircleIcon className="h-16 w-16 mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              تمت الإضافة إلى السلة
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              تم إضافة "{addedBook.title}" إلى سلة التسوق
            </p>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleContinueShopping();
                }}
                className="py-2 px-4 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-md transition-colors"
              >
                مواصلة التسوق
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCheckout();
                }}
                className="py-2 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors"
              >
                إتمام الشراء
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default BookCard;
